/**
 * FileExplorer — Drive-style folder & file browser for the Documents & Forms module.
 * Breadcrumbs · Grid/List toggle · New Folder · per-item ⋮ context menus.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Folder, FolderPlus, FolderOpen, MoreVertical, ChevronRight, Home,
  LayoutGrid, List as ListIcon, Download, Eye, Edit3, Upload, History,
  Archive, Trash2, Share2, Star, FileText, FileSpreadsheet, FileImage, File,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCOPE_BADGE = {
  schools: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
  women: 'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/30 dark:text-pink-300',
  general: 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-200',
};
const SCOPE_LABEL = { schools: 'Schools', women: 'Women', general: 'General' };

const MIME_ICON = (mime = '') => {
  if (mime.startsWith('image/')) return FileImage;
  if (mime === 'application/pdf') return FileText;
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return FileSpreadsheet;
  if (mime.includes('word') || mime.includes('text')) return FileText;
  return File;
};

const fileTypeLabel = (mime = '') => {
  if (mime === 'application/pdf') return 'PDF';
  if (mime.startsWith('image/')) return 'Image';
  if (mime.includes('sheet') || mime.includes('excel')) return 'Spreadsheet';
  if (mime.includes('csv')) return 'CSV';
  if (mime.includes('word')) return 'Word';
  if (mime.includes('text')) return 'Text';
  return (mime.split('/')[1] || 'File').toUpperCase().slice(0, 10);
};

const fmtSize = (b) => {
  if (b == null) return '—';
  return b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(1)} KB`;
};

const FileMenu = ({ d, onPreview, onDownload, onShare, onEdit, onNewVersion, onHistory, onArchive, onDelete }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`file-menu-${d.id}`}
        onClick={(e) => e.stopPropagation()}>
        <MoreVertical className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenuItem onClick={() => onPreview(d)} data-testid={`file-open-${d.id}`}>
        <Eye className="h-4 w-4 mr-2" /> Open
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onDownload(d)} data-testid={`file-download-${d.id}`}>
        <Download className="h-4 w-4 mr-2" /> Download
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onShare(d)} data-testid={`file-share-${d.id}`}>
        <Share2 className="h-4 w-4 mr-2" /> Share
      </DropdownMenuItem>
      {(onEdit || onNewVersion) && <DropdownMenuSeparator />}
      {onEdit && (
        <DropdownMenuItem onClick={() => onEdit(d)} data-testid={`file-edit-${d.id}`}>
          <Edit3 className="h-4 w-4 mr-2" /> Rename / Edit details
        </DropdownMenuItem>
      )}
      {onNewVersion && (
        <DropdownMenuItem onClick={() => onNewVersion(d)}>
          <Upload className="h-4 w-4 mr-2" /> Upload new version
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={() => onHistory(d)}>
        <History className="h-4 w-4 mr-2" /> Version history
      </DropdownMenuItem>
      {(onArchive || onDelete) && <DropdownMenuSeparator />}
      {onArchive && d.status !== 'archived' && (
        <DropdownMenuItem onClick={() => onArchive(d)} data-testid={`file-archive-${d.id}`}>
          <Archive className="h-4 w-4 mr-2" /> Archive
        </DropdownMenuItem>
      )}
      {onDelete && (
        <DropdownMenuItem onClick={() => onDelete(d)} className="text-red-600 focus:text-red-600" data-testid={`file-delete-${d.id}`}>
          <Trash2 className="h-4 w-4 mr-2" /> Delete
        </DropdownMenuItem>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

const FolderMenu = ({ f, onOpen, onRename, onUpload, onDownload, onDelete }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`folder-menu-${f.id}`}
        onClick={(e) => e.stopPropagation()}>
        <MoreVertical className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenuItem onClick={() => onOpen(f.id)} data-testid={`folder-open-${f.id}`}>
        <FolderOpen className="h-4 w-4 mr-2" /> Open
      </DropdownMenuItem>
      {onRename && (
        <DropdownMenuItem onClick={() => onRename(f)} data-testid={`folder-rename-${f.id}`}>
          <Edit3 className="h-4 w-4 mr-2" /> Rename
        </DropdownMenuItem>
      )}
      {onUpload && (
        <DropdownMenuItem onClick={() => onUpload(f.id)} data-testid={`folder-upload-${f.id}`}>
          <Upload className="h-4 w-4 mr-2" /> Upload here
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={() => onDownload(f)} data-testid={`folder-download-${f.id}`}>
        <Download className="h-4 w-4 mr-2" /> Download (.zip)
      </DropdownMenuItem>
      {onDelete && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onDelete(f)} className="text-red-600 focus:text-red-600" data-testid={`folder-delete-${f.id}`}>
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

const FileExplorer = ({
  folders, docs, currentFolderId, onNavigate, searchText,
  view, setView, canEdit, canDelete, categoryLabel,
  onDownload, onPreview, onEdit, onNewVersion, onHistory,
  onArchive, onDelete, onShare, onUploadTo, refresh,
}) => {
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const searching = !!(searchText || '').trim();

  const childFolders = useMemo(() => {
    if (searching) {
      const s = searchText.trim().toLowerCase();
      return folders.filter((f) => f.name.toLowerCase().includes(s));
    }
    return folders.filter((f) => (f.parent_id || null) === (currentFolderId || null));
  }, [folders, currentFolderId, searchText, searching]);

  const breadcrumb = useMemo(() => {
    const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
    const chain = [];
    let cur = currentFolderId ? byId[currentFolderId] : null;
    let guard = 0;
    while (cur && guard < 20) {
      chain.unshift(cur);
      cur = cur.parent_id ? byId[cur.parent_id] : null;
      guard += 1;
    }
    return chain;
  }, [folders, currentFolderId]);

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error('Folder name required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/documents/folders`, { name, parent_id: currentFolderId || null });
      toast.success(`Folder "${name}" created`);
      setNewFolderOpen(false);
      setNewFolderName('');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create folder');
    } finally { setBusy(false); }
  };

  const renameFolder = async () => {
    const name = renameValue.trim();
    if (!name) { toast.error('Folder name required'); return; }
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/documents/folders/${renameTarget.id}`, { name });
      toast.success('Folder renamed');
      setRenameTarget(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    } finally { setBusy(false); }
  };

  const deleteFolder = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API_URL}/api/documents/folders/${deleteFolderTarget.id}`);
      toast.success(`Folder deleted (${r.data.files_deleted} file${r.data.files_deleted !== 1 ? 's' : ''} removed)`);
      if (currentFolderId === deleteFolderTarget.id) onNavigate(deleteFolderTarget.parent_id || null);
      setDeleteFolderTarget(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const downloadFolder = async (f) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/folders/${f.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${f.name}.zip`; document.body.appendChild(a);
      a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Folder downloaded');
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'This folder has no downloadable files' : 'Download failed');
    }
  };

  const openFolderMenuActions = {
    onOpen: onNavigate,
    onRename: canEdit ? (f) => { setRenameTarget(f); setRenameValue(f.name); } : null,
    onUpload: onUploadTo || null,
    onDownload: downloadFolder,
    onDelete: canDelete ? setDeleteFolderTarget : null,
  };

  const fileMenuActions = { onPreview, onDownload, onShare, onEdit, onNewVersion, onHistory, onArchive, onDelete };

  const empty = childFolders.length === 0 && docs.length === 0;

  return (
    <div className="space-y-4" data-testid="file-explorer">
      {/* Breadcrumb + toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 text-sm flex-wrap" data-testid="explorer-breadcrumb">
          {searching ? (
            <span className="text-muted-foreground">Search results for “{searchText.trim()}”</span>
          ) : (
            <>
              <button onClick={() => onNavigate(null)} className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="breadcrumb-home">
                <Home className="h-3.5 w-3.5" /> Home
              </button>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              <button onClick={() => onNavigate(null)}
                className={breadcrumb.length === 0 ? 'font-medium' : 'text-muted-foreground hover:text-foreground transition-colors'}
                data-testid="breadcrumb-root">
                Documents &amp; Forms
              </button>
              {breadcrumb.map((f, i) => (
                <React.Fragment key={f.id}>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <button onClick={() => onNavigate(f.id)}
                    className={i === breadcrumb.length - 1 ? 'font-medium' : 'text-muted-foreground hover:text-foreground transition-colors'}
                    data-testid={`breadcrumb-${f.id}`}>
                    {f.name}
                  </button>
                </React.Fragment>
              ))}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !searching && (
            <Button variant="outline" size="sm" onClick={() => setNewFolderOpen(true)} data-testid="new-folder-btn">
              <FolderPlus className="h-4 w-4 mr-1.5" /> New Folder
            </Button>
          )}
          <div className="flex rounded-md border overflow-hidden">
            <Button variant={view === 'grid' ? 'secondary' : 'ghost'} size="sm" className="rounded-none h-8"
              onClick={() => setView('grid')} title="Grid view" data-testid="view-grid-btn">
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button variant={view === 'list' ? 'secondary' : 'ghost'} size="sm" className="rounded-none h-8"
              onClick={() => setView('list')} title="List view" data-testid="view-list-btn">
              <ListIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {empty ? (
        <Card>
          <CardContent className="py-14 text-center text-muted-foreground">
            <Folder className="h-10 w-10 mx-auto mb-2 opacity-40" />
            {searching ? 'No folders or files match your search.' : 'This folder is empty. Create a folder or upload a document to get started.'}
          </CardContent>
        </Card>
      ) : view === 'grid' ? (
        <div className="space-y-5">
          {childFolders.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Folders</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {childFolders.map((f) => (
                  <Card key={f.id} className="cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all"
                    onClick={() => onNavigate(f.id)} data-testid={`folder-card-${f.id}`}>
                    <CardContent className="p-3 flex items-center gap-2.5">
                      <Folder className="h-8 w-8 text-amber-500 fill-amber-100 dark:fill-amber-900/40 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate" title={f.name}>{f.name}</div>
                        <div className="text-xs text-muted-foreground">{f.file_count} file{f.file_count !== 1 ? 's' : ''}</div>
                      </div>
                      <FolderMenu f={f} {...openFolderMenuActions} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          {docs.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Files</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {docs.map((d) => {
                  const Icon = MIME_ICON(d.file_type);
                  return (
                    <Card key={d.id} className="cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all"
                      onClick={() => onPreview(d)} data-testid={`file-card-${d.id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-2.5">
                          <Icon className="h-8 w-8 text-primary/70 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate flex items-center gap-1" title={d.name}>
                              {d.name}
                              {d.is_quick_form && <Star className="h-3 w-3 text-amber-500 fill-amber-400 flex-shrink-0" />}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {fileTypeLabel(d.file_type)} · {fmtSize(d.file_size)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(d.created_at || d.updated_at).toLocaleDateString()}
                            </div>
                          </div>
                          <FileMenu d={d} {...fileMenuActions} />
                        </div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <Badge variant="outline" className={`text-[10px] py-0 ${SCOPE_BADGE[d.scope] || SCOPE_BADGE.general}`}>
                            {SCOPE_LABEL[d.scope] || 'General'}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] py-0">{categoryLabel[d.category] || d.category}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Date Uploaded</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {childFolders.map((f) => (
                  <TableRow key={f.id} className="cursor-pointer hover:bg-muted/30" onClick={() => onNavigate(f.id)} data-testid={`folder-row-${f.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium">
                        <Folder className="h-4 w-4 text-amber-500 fill-amber-100 dark:fill-amber-900/40" /> {f.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">Folder</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{f.file_count} file{f.file_count !== 1 ? 's' : ''}</TableCell>
                    <TableCell className="text-xs">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">—</span></TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">—</span></TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <FolderMenu f={f} {...openFolderMenuActions} />
                    </TableCell>
                  </TableRow>
                ))}
                {docs.map((d) => {
                  const Icon = MIME_ICON(d.file_type);
                  return (
                    <TableRow key={d.id} className="cursor-pointer hover:bg-muted/30" onClick={() => onPreview(d)} data-testid={`doc-row-${d.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-primary/70" />
                          <div>
                            <div className="font-medium flex items-center gap-1">
                              {d.name}
                              {d.is_quick_form && <Star className="h-3 w-3 text-amber-500 fill-amber-400" />}
                            </div>
                            <div className="text-xs text-muted-foreground">{d.file_name} · v{d.version}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{fileTypeLabel(d.file_type)}</TableCell>
                      <TableCell className="text-sm">{fmtSize(d.file_size)}</TableCell>
                      <TableCell className="text-xs">
                        <div>{new Date(d.created_at || d.updated_at).toLocaleDateString()}</div>
                        <div className="text-muted-foreground">{d.uploaded_by_name}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={SCOPE_BADGE[d.scope] || SCOPE_BADGE.general}>
                          {SCOPE_LABEL[d.scope] || 'General'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{categoryLabel[d.category] || d.category}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <FileMenu d={d} {...fileMenuActions} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={(v) => !v && setNewFolderOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="new-folder-dialog">
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
            <DialogDescription>
              {breadcrumb.length > 0 ? `Created inside "${breadcrumb[breadcrumb.length - 1].name}"` : 'Created at the top level'}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Folder name</Label>
            <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createFolder()}
              placeholder="e.g. Finance" autoFocus data-testid="new-folder-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>Cancel</Button>
            <Button onClick={createFolder} disabled={busy || !newFolderName.trim()} data-testid="new-folder-submit">
              {busy ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename folder dialog */}
      {renameTarget && (
        <Dialog open onOpenChange={(v) => !v && setRenameTarget(null)}>
          <DialogContent className="max-w-sm" data-testid="rename-folder-dialog">
            <DialogHeader><DialogTitle>Rename folder</DialogTitle></DialogHeader>
            <div>
              <Label>Folder name</Label>
              <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && renameFolder()}
                autoFocus data-testid="rename-folder-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
              <Button onClick={renameFolder} disabled={busy || !renameValue.trim()} data-testid="rename-folder-submit">
                {busy ? 'Saving…' : 'Rename'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete folder confirm */}
      {deleteFolderTarget && (
        <AlertDialog open onOpenChange={(v) => !v && setDeleteFolderTarget(null)}>
          <AlertDialogContent data-testid="delete-folder-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete “{deleteFolderTarget.name}”?</AlertDialogTitle>
              <AlertDialogDescription>
                The folder, all its sub-folders and every file inside will be permanently deleted. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteFolder} className="bg-red-600 hover:bg-red-700" data-testid="delete-folder-confirm">
                Delete folder
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default FileExplorer;
